import React, { useCallback, useState } from 'react'
import { useStyles } from '../../../PrivateOfferForm/ViewPrivateOffer/ViewPrivateOffer.styles'
import { Typography } from '@mui/material'
import {
  ModalConfirm,
  ModalSizeVariants,
  PlanType,
} from '@labrav/react-components'
import clsx from 'clsx'
import { dateAndTimeConversion } from '../../../../common/utils/dateAndTimeConversion'
import { useSelector } from '../../../../store'
import { ResaleAuthorizationsResellerAgreements } from '../../../../admin/modules/resaleAuthorizations'
import { Recipients } from '../../../PrivateOfferForm/Recipients/Recipients'
import {
  DetailedOffer,
  type DetailedOffer as DetailedOfferData,
} from '../../../PrivateOfferForm/DetailedOffer/DetailedOffer'
import { getActiveStepLable } from '../../helpers'
import {
  getIsCustomPricing,
  getResaleOfferForDetailedView,
} from '../../../../admin/components/ResaleAuthorizationsList/helpers'
import { getPricesArray } from '../../../../modules/resaleAuthorization/action'
import { PriceTypes } from '../../../../types/flyout'
import { get, isEmpty } from 'lodash'
import type { RootState } from '../../../../store/types'

export type ViewResaleAuthorizationProps = {
  resaleAuthorization: ResaleAuthorizationsResellerAgreements
  planType: PlanType
}
export const ViewResaleAuthorization: React.FC<
  ViewResaleAuthorizationProps
> = ({ resaleAuthorization, planType }) => {
  const { classes } = useStyles()
  const timeZone = useSelector(
    (state: RootState) =>
      state.userProfile.userProfile.timeZone ||
      state.userProfile.userProfile.defaultTimeZone ||
      'UTC'
  )
  const [offerData, setOfferData] = useState<DetailedOfferData>(
    {} as DetailedOfferData
  )

  const { modifiedDate: offerCreatedAt } = dateAndTimeConversion(
    resaleAuthorization.createdAt,
    timeZone
  )
  const { modifiedDate: offerExpirationDate } = dateAndTimeConversion(
    resaleAuthorization.agreementExpirationDate,
    timeZone
  )
  const [openModal, setOpenModal] = useState<boolean>(false)
  const viewDetailedOffer = useCallback(() => {
    setOfferData(
      getResaleOfferForDetailedView({ ...resaleAuthorization }, timeZone)
    )
    setOpenModal(true)
  }, [])
  const handleClose = useCallback(() => {
    setOpenModal(false)
  }, [openModal])
  const isCustomPricing = getIsCustomPricing(
    resaleAuthorization?.discountType,
    resaleAuthorization?.contractDurationType
  )
  const pricesArray = (
    isCustomPricing
      ? ['price']
      : getPricesArray(get(resaleAuthorization?.products, '[0].dimensions', []))
  ) as [keyof PriceTypes]

  const showOfferExpirationDate = !isEmpty(
    resaleAuthorization.agreementExpirationDate
  )
    ? offerExpirationDate
    : 'N.A'
  return (
    <>
      <div className={classes.root} data-testid="resale-authorization-details">
        <div className={classes.offerGeneral}>
          <div
            className={clsx(
              classes.offerGeneralRow,
              classes.offerNameContainer
            )}
          >
            <KeyValueSection
              customLabelClass={classes.offerNameAlign}
              customValueClass={clsx(
                classes.offerNameAlign,
                classes.offerNameValue
              )}
              wrapperClass={classes.wrapperOfferName}
              LabelName={'Authorization name'}
              value={resaleAuthorization.agreementName}
              testId={'authorization-name'}
            />
            <Typography
              className={classes.viewMoreText}
              onClick={viewDetailedOffer}
              data-testid="view-more-details"
            >
              View more details
            </Typography>
          </div>

          <KeyValueSection
            LabelName={'Labra authorization ID'}
            value={resaleAuthorization.id}
            testId={'authorization-labra-id'}
            wrapperClass={clsx(
              classes.offerGeneralRow,
              classes.generalRowPadding
            )}
          />
          <KeyValueSection
            LabelName={'Authorization status'}
            value={getActiveStepLable(resaleAuthorization.status)}
            testId={'authorization-status'}
            wrapperClass={clsx(
              classes.offerGeneralRow,
              classes.generalRowPadding
            )}
          />
          <div
            className={
              resaleAuthorization.awsAgreementId
                ? classes.offerGeneralRowDivideContainer
                : classes.offerGeneralRow
            }
          >
            <KeyValueSection
              LabelName={'Reseller name'}
              value={
                resaleAuthorization.companyName ||
                get(
                  resaleAuthorization,
                  'resellersInfo[0].resellerCompanyName',
                  ''
                )
              }
              testId={'authorization-reseller-name'}
              wrapperClass={
                resaleAuthorization.awsAgreementId
                  ? classes.offerGeneralRowDivide
                  : ''
              }
            />

            {resaleAuthorization.awsAgreementId && (
              <KeyValueSection
                LabelName={'AWS authorization ID'}
                value={resaleAuthorization.awsAgreementId}
                testId={'authorization-aws-id'}
                wrapperClass={clsx(
                  classes.offerGeneralRowDivide,
                  classes.noRightBorder
                )}
              />
            )}
          </div>
          <div className={classes.offerGeneralRowDivideContainer}>
            <KeyValueSection
              LabelName={'Created on'}
              value={offerCreatedAt}
              testId={'authorization-created-at'}
              wrapperClass={classes.offerGeneralRowDivide}
            />
            <KeyValueSection
              LabelName={'Authorization end date'}
              value={showOfferExpirationDate}
              testId={'authorization-expiration'}
              wrapperClass={clsx(
                classes.offerGeneralRowDivide,
                classes.noRightBorder
              )}
            />
          </div>
        </div>

        <Recipients
          recipients={resaleAuthorization.agreementRecipients}
          timeZone={timeZone}
          offerId={resaleAuthorization.id}
          offerExpirationDate={resaleAuthorization.agreementExpirationDate}
          planType={planType}
          isResale={true}
          awsOfferId={resaleAuthorization?.awsAgreementId}
          resaleStatus={resaleAuthorization.status}
          agreementDurationType={resaleAuthorization?.agreementDurationType}
        />
      </div>
      <ModalConfirm
        title={'Preview'}
        subTitle={''}
        content={<DetailedOffer offer={offerData} pricesArray={pricesArray} />}
        open={openModal}
        titleAcceptButton={'Okay'}
        onAccept={handleClose}
        onDecline={handleClose}
        variant={ModalSizeVariants.medium}
        hideCancelButton={true}
      />
    </>
  )
}

export type KeyValueSectionProps = {
  LabelName: string
  value: string
  testId: string
  wrapperClass?: string
  customLabelClass?: string
  customValueClass?: string
}
export const KeyValueSection = ({
  LabelName,
  value,
  testId,
  wrapperClass,
  customLabelClass,
  customValueClass,
}: KeyValueSectionProps) => {
  const { classes } = useStyles()
  return (
    <div className={wrapperClass || ''}>
      <Typography
        className={clsx(classes.offerGeneralLabel, customLabelClass || '')}
        data-testid={`${testId}-label`}
      >
        {LabelName}
      </Typography>
      <Typography
        className={clsx(classes.offerGeneralValue, customValueClass || '')}
        data-testid={`${testId}-value`}
      >
        {value}
      </Typography>
    </div>
  )
}
